import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppDispatch } from 'store/store';
import { contextActions } from 'store/slices/contextSlice';
import { PageMetaDatas, PageType } from 'components/page-context/PageMetaDatas';
import { extractEntityContext } from 'components/page-context/entityContextBuilders';
import { buildFormSection } from 'components/page-context/formContextBuilders';

interface TelemetryParams {
  title: string;
  entityCode?: string;
  pageType?: PageType;
  pageRef: React.RefObject<HTMLDivElement | null>;
}

/**
 * Handles title updates, Redux context sync, DOM mutations observation,
 * and custom debounced AI telemetry context updates ('ava-context-update').
 */
export function useTenantPageTelemetry({ title, entityCode, pageType, pageRef }: TelemetryParams) {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const page = pageType ? PageMetaDatas[pageType] : undefined;

  // Update document metadata title
  useEffect(() => {
    document.title = title;
  }, [title]);

  // Synchronize Redux contextual identity identifiers
  useEffect(() => {
    if (entityCode) {
      dispatch(contextActions.setFriendlyId(entityCode));
    }

    return () => {
      dispatch(contextActions.setFriendlyId(null));
    };
  }, [entityCode, dispatch]);

  // Sync state values into mutable container ref to completely bypass stale closure loops
  const latestDataRef = useRef({ page, title });
  useEffect(() => {
    latestDataRef.current = { page, title };
  }, [page, title]);

  // Handle Event Tracing and Mutation Observers
  useEffect(() => {
    const mountedPathname = location.pathname;

    let contextTimeout: NodeJS.Timeout;

    const dispatchBaseContext = () => {
      const { page: latestPage, title: latestTitle } = latestDataRef.current;

      const dataGridElement = pageRef.current?.querySelector('.MuiDataGrid-root');
      const hasDataGrid = !!dataGridElement;

      let contextText = '';

      if (!hasDataGrid) {
        const fullUrl = `${window.location.origin}${location.pathname}${location.search}`;
        const entityContext = extractEntityContext(fullUrl);

        if (entityContext?.entityId) {
          const formFields = buildFormSection();
          const sections = [
            `### ${latestTitle ? latestTitle.toUpperCase() : 'PAGINA'}`,
            `URL: ${entityContext.url}`,
            entityContext.entityId &&
              `${entityContext.modelMappingHint}|Possible object_id=${entityContext.entityId}`,
            latestPage?.description && `PURPOSE: ${latestPage.description}`,
            latestPage?.key_actions && `ACTIONS: ${latestPage.key_actions}`,
            formFields.length > 0 && `FORM FIELDS:\n${formFields.join('\n')}`
          ];
          contextText = sections.filter(Boolean).join('\n');
        }
      }

      window.dispatchEvent(
        new CustomEvent('ava-context-update', {
          detail: {
            text: contextText,
            url: mountedPathname
          }
        })
      );
    };

    const triggerDebouncedUpdate = () => {
      clearTimeout(contextTimeout);
      contextTimeout = setTimeout(dispatchBaseContext, 200);
    };

    const handleInputChange = (e: Event) => {
      const target = e.target as HTMLElement;
      if (target.matches('input, textarea, select') || target.closest('.MuiAutocomplete-root')) {
        triggerDebouncedUpdate();
      }
    };

    window.addEventListener('input', handleInputChange, true);
    window.addEventListener('change', handleInputChange, true);

    const observer = new MutationObserver((mutations) => {
      triggerDebouncedUpdate();
    });

    if (pageRef.current) {
      observer.observe(pageRef.current, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: false
      });
    }

    contextTimeout = setTimeout(dispatchBaseContext, 50);

    return () => {
      clearTimeout(contextTimeout);
      observer.disconnect();
      window.removeEventListener('input', handleInputChange, true);
      window.removeEventListener('change', handleInputChange, true);

      window.dispatchEvent(
        new CustomEvent('ava-context-update', {
          detail: {
            text: '',
            url: mountedPathname
          }
        })
      );
    };
  }, [location.pathname, location.search]);

  return { page };
}
